/* eslint-disable max-lines-per-function */
import {Swiper, SwiperSlide} from 'swiper/react';
import SwiperCore from 'swiper';
import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import cs from 'classnames';
import {Autoplay, FreeMode, Mousewheel, Pagination, Zoom} from 'swiper/modules';
import {MIN_SWIPE_DISTANCE, SPACE_BETWEEN} from 'modules/slider/constants';
import {useIsSkeleton} from 'modules/skeleton/hooks/useIsSkeleton';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import style from './style.module.scss';
import Icon from 'components/Icon';
import {PaginationOptions, SwiperOptions} from 'swiper/types';
import {SwiperClass} from 'swiper/swiper-react';

interface IProps extends SwiperOptions {
    className?: string;
    children: ReactNode[];
    isSlideTo?: boolean;
    isPagination?: boolean;
    isFlatPagination?: boolean;
    isNavigation?: boolean;
    onInitialized?: (swiper: SwiperClass) => void;
    isSliderButtonsDefault?: boolean;
    isWhiteNavButtons?: boolean;
    renderBullet?: (index: number, className: string) => string;
    magnifierCursor?: ReactNode;
    navButton?: JSX.Element;
    onSlideChange?: () => void;
    classNames?: {
        navButton?: string;
    };
}

export const Slider = ({
    children,
    className,
    isSlideTo = false,
    isPagination = false,
    isNavigation = false,
    isSliderButtonsDefault = true,
    spaceBetween,
    onInitialized,
    isFlatPagination = false,
    isWhiteNavButtons = false,
    renderBullet,
    zoom,
    modules: modulesProp,
    freeMode,
    magnifierCursor,
    loop,
    navButton,
    classNames,
    onSlideChange,
    ...sliderProps
}: IProps) => {
    const [activeIdx, setActiveIdx] = useState<number>(0);

    const slider = useRef<SwiperClass | undefined>();

    const navPrevButton = useRef<HTMLDivElement | null>(null);

    const navNextButton = useRef<HTMLDivElement | null>(null);

    const isSkeleton = useIsSkeleton();

    const [swiper, seSwiperClass] = useState<SwiperCore | null>(null);
    const [isPrevDisabled, setIsPrevDisabled] = useState<boolean>(!loop);
    const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);

    useEffect(() => {
        if (navPrevButton?.current && !loop) {
            navPrevButton.current?.classList.add(style.navDisabled);
        }
    }, [loop]);

    useEffect(() => {
        if (isSlideTo && Boolean(slider.current)) {
            slider.current?.slideTo(activeIdx);
        }
    }, [activeIdx, isSlideTo, slider]);

    const setNavButtonsState = useCallback(() => {
        if (swiper && !loop) {
            setIsPrevDisabled(swiper.isBeginning);
            setIsNextDisabled(swiper.isEnd);
        }
    }, [loop, swiper]);

    const onPrevSlide = useCallback(() => {
        if (swiper) {
            setNavButtonsState();
            swiper.slidePrev();
        }
    }, [setNavButtonsState, swiper]);

    const onNextSlide = useCallback(() => {
        if (swiper) {
            setNavButtonsState();
            swiper.slideNext();
        }
    }, [setNavButtonsState, swiper]);

    const onSwiper = useCallback(
        (swiperElement: SwiperClass) => {
            if (slider) {
                slider.current = swiperElement;
            }

            seSwiperClass(swiperElement);

            onInitialized?.(swiperElement);
        },
        [onInitialized],
    );

    const onActiveIndexChange = useCallback(
        (swiperElement: SwiperClass) => {
            setNavButtonsState();
            onSlideChange?.();
            setActiveIdx(swiperElement.activeIndex);
        },
        [onSlideChange, setNavButtonsState],
    );

    const renderSlides = useMemo<ReactNode[]>(
        () => children.map((child, index) => <SwiperSlide key={index}>{child}</SwiperSlide>),
        [children],
    );

    const renderModules = useMemo<SwiperOptions['modules']>(() => {
        const modules = [Mousewheel];

        if (sliderProps.autoplay) {
            modules.push(Autoplay);
        }

        if (isPagination) {
            modules.push(Pagination);
        }

        if (zoom) {
            modules.push(Zoom);
        }

        if (freeMode) {
            modules.push(FreeMode);
        }

        if (modulesProp?.length) {
            modules.push(...modulesProp);
        }

        return modules;
    }, [freeMode, isPagination, modulesProp, sliderProps.autoplay, zoom]);

    const paginationParams = useMemo<PaginationOptions | boolean>(
        () =>
            isPagination && {
                clickable: true,
                dynamicBullets: false,
                renderBullet,
            },
        [isPagination, renderBullet],
    );

    const navIcon = useMemo(() => {
        if (navButton) {
            return navButton;
        }

        return isWhiteNavButtons ? <Icon icon={'arrow-triangle'} /> : <Icon icon={'arrow-sm'} />;
    }, [isWhiteNavButtons, navButton]);

    const isNavHidden = useMemo(
        () => !loop && swiper?.isEnd && swiper?.isBeginning,
        [loop, swiper?.isBeginning, swiper?.isEnd],
    );

    const isSpaceBetweenIncluded = useMemo(
        () =>
            sliderProps.breakpoints
                ? Object.values(sliderProps.breakpoints).some((breakpoint) => Boolean(breakpoint.spaceBetween))
                : false,
        [sliderProps.breakpoints],
    );

    return (
        <Swiper
            {...sliderProps}
            className={cs(className, style.slider, {
                [style.flatPagination]: Boolean(isPagination && isFlatPagination),
            })}
            enabled={!isSkeleton}
            freeMode={freeMode}
            loop={loop}
            modules={renderModules}
            mousewheel={{
                forceToAxis: true,
            }}
            onActiveIndexChange={onActiveIndexChange}
            onSwiper={onSwiper}
            pagination={paginationParams}
            spaceBetween={!isSpaceBetweenIncluded ? (spaceBetween ?? SPACE_BETWEEN.default) : undefined}
            threshold={MIN_SWIPE_DISTANCE}
        >
            {magnifierCursor}
            {renderSlides}
            {isNavigation && !isNavHidden && !isSkeleton && (
                <>
                    <div
                        className={cs(
                            classNames?.navButton,
                            style.navButton,
                            style.sliderButtonPrev,
                            'nav-button',
                            'nav-button-prev',
                            {
                                [style.whiteBtn]: isWhiteNavButtons,
                                [style.navDisabled]: loop ? false : isPrevDisabled,
                            },
                        )}
                        onClick={onPrevSlide}
                        ref={navPrevButton}
                    >
                        {navIcon}
                    </div>
                    <div
                        className={cs(
                            classNames?.navButton,
                            style.navButton,
                            style.sliderButtonNext,
                            'nav-button',
                            'nav-button-next',
                            {
                                [style.whiteBtn]: isWhiteNavButtons,
                                [style.navDisabled]: loop ? false : isNextDisabled,
                            },
                        )}
                        onClick={onNextSlide}
                        ref={navNextButton}
                    >
                        {navIcon}
                    </div>
                </>
            )}
        </Swiper>
    );
};
